import { colors } from './colors';
import { fonts } from './fonts';
import { fontSizes } from './fontSizes';
import { shadows } from './shadows';
import { IButtons, IDefaultTextButton } from './IButtons';

const defaultKind: IDefaultTextButton = {
  color: colors.light,
  bgColor: colors.primary,
  fontFamily: fonts.primary,
  fontWeight: 'normal',
  activeOpacity: 0.2,
};

const commonCriteriaTag = {
  ...defaultKind,
  activeOpacity: 1,
  fontFamily: fonts.primary,
  fontWeight: 600,
};

export const buttons: IButtons = {
  text: {
    kinds: {
      default_primary: defaultKind,
      header: {
        ...defaultKind,
        bgColor: 'rgba(0,0,0,0)',
        borderColor: colors.light,
        borderWidth: 1,
      },
      validate: {
        ...defaultKind,
        color: colors.primary,
        bgColor: colors.light,
        borderRadius: '5px',
        fontFamily: fonts.primary,
        fontWeight: 600,
        minWidth: '216px',
      },
      photo_request: {
        ...defaultKind,
        bgColor: colors.lightTranslucent,
        fontFamily: fonts.primary,
        fontWeight: 600,
        minWidth: '216px',
      },
      profile_capture_choice: {
        ...defaultKind,
        bgColor: colors.lightTranslucent,
        fontFamily: fonts.primary,
        fontWeight: 600,
      },
      card_action: {
        ...defaultKind,
        bgColor: colors.accent,
        fontFamily: fonts.primary,
        fontWeight: 600,
        fontSize: fontSizes.size16Int,
        padding: '0px 20px',
        minWidth: '108px',
      },
      criteria_tag: {
        ...commonCriteriaTag,
        color: colors.secondary,
        bgColor: colors.alabaster,
      },
      criteria_tag_selected: {
        ...commonCriteriaTag,
        color: colors.accent,
        bgColor: colors.pippin,
      },
      profile_display_tag: {
        ...defaultKind,
        color: colors.primary,
        bgColor: colors.light,
        borderColor: colors.gallery,
        borderWidth: 2,
        activeOpacity: 1,
      },
      profile_display_tag_selected: {
        ...defaultKind,
        color: colors.primary,
        bgColor: colors.light,
        borderColor: colors.hippieBlue,
        borderWidth: 2,
        activeOpacity: 1,
      },
      subscribe: {
        ...defaultKind,
        bgColor: colors.caribbeanGreen,
        fontFamily: fonts.primary,
        fontWeight: 600,
        minWidth: '216px',
      },
      pay: {
        ...defaultKind,
        bgColor: colors.caribbeanGreen,
        fontFamily: fonts.primary,
        fontWeight: 600,
      },
      retry: {
        ...defaultKind,
        bgColor: colors.accent,
        fontFamily: fonts.primary,
        fontWeight: 700,
        minWidth: '22px',
        padding: '0px 8px 0px 9px',
        fontSize: 16,
      },
      redirect: {
        ...defaultKind,
        bgColor: colors.accent,
        fontFamily: fonts.primary,
        fontWeight: 600,
      },
      primary: {
        ...defaultKind,
        bgColor: colors.accent,
        fontFamily: fonts.primary,
        fontWeight: 600,
        minWidth: '216px',
      },
      primary_ghost: {
        ...defaultKind,
        bgColor: 'rgba(0, 0, 0, 0)',
        fontFamily: fonts.primary,
        fontWeight: 600,
        color: colors.accent,
        borderColor: colors.accent,
        borderWidth: 2,
        minWidth: '216px',
      },
      modal_cancel: {
        ...defaultKind,
        color: colors.accent,
        bgColor: colors.lightTranslucent,
        minWidth: '216px',
      },
      navigation_dark: {
        ...defaultKind,
        color: colors.light,
        bgColor: colors.pc_darkTranslucent,
        padding: '4px 16px 4px 16px',
        fontFamily: fonts.primary,
        fontWeight: 600,
      },
      unblock: {
        ...defaultKind,
        color: colors.light,
        bgColor: colors.accent,
        fontFamily: fonts.primary,
        fontWeight: 600,
        borderRadius: '0px',
        padding: '10px 0px',
        minWidth: '100%',
      },
    },
    sizes: {
      xsmall: { padding: '1px 10px', fontSize: 12 },
      small: { padding: '4px 20px', fontSize: 14 },
      medium: { padding: '8px 20px', fontSize: 16 },
      large: { padding: '8px 20px', fontSize: 18 },
      xlarge: { padding: '8px 80px', fontSize: 22 },
    },
  },
  icon: {
    kinds: {
      shortcut: {
        color: colors.primary,
        colorPressed: colors.light,
        bgColor: colors.light,
        bgColorPressed: colors.primary,
        shadowWeb: shadows.shadowWeb,
        shadowNative: shadows.shadowNative,
      },
      navigation_light: {
        color: colors.light,
        colorPressed: colors.light,
        bgColor: colors.lightTranslucent,
        bgColorPressed: colors.lightHover,
        shadowWeb: '',
        shadowNative: '',
      },
      shortcut_primary: {
        color: colors.accent,
        colorPressed: colors.accent,
        bgColor: colors.light,
        bgColorPressed: colors.lightHover,
        shadowWeb: shadows.shadowWeb,
        shadowNative: shadows.shadowNative,
      },
      navigation_dark: {
        color: colors.light,
        colorPressed: colors.light,
        bgColor: colors.pc_darkTranslucent,
        bgColorPressed: colors.pc_darkTranslucent,
        shadowWeb: '',
        shadowNative: '',
      },
      cta: {
        color: colors.light,
        colorPressed: colors.light,
        bgColor: colors.caribbeanGreen,
        bgColorPressed: colors.caribbeanGreen,
        shadowWeb: '',
        shadowNative: '',
      },
      card_decline: {
        color: colors.secondary,
        colorPressed: colors.accent,
        bgColor: colors.light,
        bgColorPressed: colors.lightHover,
        shadowWeb: shadows.shadowWeb,
        shadowNative: shadows.shadowNative,
      },
      card_accept: {
        color: colors.accent,
        colorPressed: colors.accent,
        bgColor: colors.light,
        bgColorPressed: colors.lightHover,
        shadowWeb: shadows.shadowWeb,
        shadowNative: shadows.shadowNative,
      },
    },
    sizes: {
      xsmall: 30,
      small: 35,
      medium: 56,
      large: 70,
      xlarge: 80,
    },
  },
  iconText: {
    kinds: {
      dark_translucent: {
        container: {
          activeOpacity: 0.2,
          bgColor: colors.pc_darkTranslucent,
          paddingHorizontal: 22,
        },
        icon: {
          marginRight: 4,
        },
        label: {
          fontFamily: fonts.primary,
          fontWeight: 600,
        },
        shared: {
          color: colors.light,
        },
      },
      card_single_action: {
        container: {
          activeOpacity: 0.7,
          bgColor: colors.light,
          paddingHorizontal: 27,
          shadowWeb: shadows.shadowWeb,
          shadowNative: shadows.shadowNative,
        },
        icon: {
          marginRight: 4,
        },
        label: {
          fontFamily: fonts.primary,
          fontWeight: 600,
        },
        shared: {
          color: colors.accent,
        },
      },
    },
    sizes: {
      xsmall: { height: 14, fontSize: 12 },
      small: { height: 20, fontSize: 15 },
      medium: { height: 25, fontSize: 15 },
      large: { height: 32, fontSize: 18 },
      xlarge: { height: 40, fontSize: 22 },
    },
  },
};
